// context/TrayContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axiosApexInstance from '../auth/axiosApexInstance';
import { OriginContextItem } from '../types';
import { useUser } from './UserContext';

export interface TrayItem {
    trayId?: number;
    contextType: string;
    contextId: number;
    context?: OriginContextItem;
    label?: string;
    icon?: React.ReactNode;
    contextJson?: string;
}

interface TrayContextType {
    trayItems: TrayItem[];
    addToTray: (item: TrayItem) => Promise<void>;
    removeFromTray: (contextType: string, contextId: number) => Promise<void>;
    loadTray: (userId: number) => Promise<void>;
}

const TrayContext = createContext<TrayContextType | undefined>(undefined);

export const TrayProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { apexUser } = useUser();
    const [trayItems, setTrayItems] = useState<TrayItem[]>([]);

    const loadTray = async (userId: number) => {
        try {
            const res = await axiosApexInstance.get(`/task/tray/list?userId=${userId}`);

            const trayWithContext = await Promise.all(
                res.data.map(async (entry: TrayItem) => {
                    if (entry.context) return entry;

                    if (entry.contextJson) {
                        try {
                            return { ...entry, context: JSON.parse(entry.contextJson) };
                        } catch (err) {
                            console.warn('Failed to parse contextJson', entry, err);
                        }
                    }

                    try {
                        const detailRes = await axiosApexInstance.get('/task/triage/detail', {
                            params: { contextType: entry.contextType, contextId: entry.contextId }
                        });
                        return { ...entry, context: detailRes.data };
                    } catch (err) {
                        console.warn('Failed to fetch detail for tray item', entry, err);
                    }

                    return entry;
                })
            );

            setTrayItems(trayWithContext);
        } catch (err) {
            console.error('Failed to load tray items', err);
        }
    };

    useEffect(() => {
        if (apexUser?.userId) {
            loadTray(apexUser.userId);
        }
    }, [apexUser?.userId]);

    const addToTray = async (item: TrayItem) => {
        if (!apexUser?.userId) return;
        try {
            await axiosApexInstance.post('/task/tray/add', {
                userId: apexUser.userId,
                contextType: item.contextType,
                contextId: item.contextId,
                contextJson: item.contextJson || JSON.stringify(item.context || {})
            });
            setTrayItems(prev => {
                const exists = prev.some(t => t.contextType === item.contextType && t.contextId === item.contextId);
                return exists ? prev : [...prev, item];
            });
        } catch (err) {
            console.error('Failed to add to tray', err);
        }
    };

    const removeFromTray = async (contextType: string, contextId: number) => {
        if (!apexUser?.userId) return;
        try {
            await axiosApexInstance.delete('/task/tray/remove', {
                params: { userId: apexUser.userId, contextType, contextId }
            });

            setTrayItems(prev =>
                prev.filter(t => !(t.contextType === contextType && t.contextId === contextId))
            );
        } catch (err) {
            console.error('Failed to remove from tray', err);
        }
    };

    return (
        <TrayContext.Provider value={{ trayItems, addToTray, removeFromTray, loadTray }}>
            {children}
        </TrayContext.Provider>
    );
};

export const useTray = (): TrayContextType => {
    const context = useContext(TrayContext);
    if (!context) {
        throw new Error('useTray must be used within a TrayProvider');
    }
    return context;
};
